import { AxiosResponse } from 'axios'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import {
  SelectedFilter,
  Filter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import {
  fetchSearchLayout,
  fetchSearchWidgetsData,
} from '@src/quince-site-common/src/services/sandSearchService/searchService'
import {
  LayoutResponse,
  PAGE_TYPES,
  WidgetFetchDataResponse,
  WidgetInfo,
} from '@src/quince-site-common/src/services/sandSearchService/searchService.types'
import { debounce } from '@src/quince-site-common/src/utils/debounce'
import { getLbUserIdWithoutColons } from '@src/quince-site-common/src/utils/getlb-user-id'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { SEARCH_WATCHED_QUERY_KEYS, SEARCH_LIMIT as LIMIT } from '../searchResults/searchResults.constants'
import {
  convertFacetsToInitialFilters,
  getSearchCompletedEventData,
  getSearchFilters,
  getUpdatedSelectedFilters,
  transformProductsToProductCards,
} from '../utils'

const DEBOUNCE_TIME = 300
// Error messages constants
const FILTER_UPDATE_ERROR_MESSAGE = 'Failed to apply search filter'
const INITIAL_SEARCH_ERROR_MESSAGE = 'Failed to fetch initial search results'
const LOAD_MORE_ERROR_MESSAGE = 'Failed to fetch more search results'

export const useSearchResults = () => {
  const [filters, setFilters] = useState<Filter[]>([])
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'q'>(
    SEARCH_WATCHED_QUERY_KEYS,
    undefined,
    true,
  )
  const [productCards, setProductCards] = useState<IProductCard[]>([])
  const [isLayoutLoading, setIsLayoutLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [isMoreProductsLoading, setIsMoreProductsLoading] = useState(false)

  const lbUserId = getLbUserIdWithoutColons()
  const currentPageRef = useRef(0)
  const totalPagesRef = useRef(0)
  const totalProductsRef = useRef(0)
  const widgetInfoRef = useRef<WidgetInfo>({})
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasMorePageToLoad = currentPageRef.current + 1 <= totalPagesRef.current

  // Extract selected filters option from URL
  const { selectedFilters, searchQuery } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const searchQuery = watchedQueryStates.q ?? ''

    return { selectedFilters, searchQuery }
  }, [watchedQueryStates])

  const shouldTriggerSearchCompleteEvent = useAppSelector<SearchDetails>(
    siteStateManager,
    siteStateManager.getSearchDetails,
  ).shouldTriggerSearchCompleteEvent

  const handleSearchAPIError = useCallback((error: unknown, errorMessage: string) => {
    captureException(error, {
      module: MODULES.SEARCH,
      feature: FEATURES.SEARCH,
      extraInfo: { message: errorMessage },
    })
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchProductsOnFilterUpdate = useCallback(
    async ({ updatedSelectedFilters }: { updatedSelectedFilters: SelectedFilter }) => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal
      // Reset pagination when filters change and fetch filtered products
      currentPageRef.current = 0
      setIsLayoutLoading(true)
      const apiFilters = getSearchFilters(updatedSelectedFilters)
      try {
        const response = await fetchSearchWidgetsData(
          {
            query: searchQuery,
            offset: 0,
            limit: LIMIT,
            lbUserId,
            pageType: PAGE_TYPES.SEARCH,
            widgetInfo: widgetInfoRef.current,
            filter: apiFilters,
          },
          { signal },
        )

        // Only update state if the request wasn't aborted
        if (!signal.aborted) {
          currentPageRef.current = 1
          totalPagesRef.current = response.data.widgets.data?.paginationMetadata?.totalPages ?? 0
          totalProductsRef.current = response.data.widgets.data?.paginationMetadata?.totalCount ?? 0

          const products = response.data.widgets.data?.products ?? []
          setProductCards(transformProductsToProductCards(products))
          siteStateManager.setSearchAlgoliaQueryId({
            searchQuery: searchQuery,
            searchAlgoliaQueryId: response.data.widgets.data?.searchEventMetadata?.queryId ?? '',
          })
        }
      } catch (e) {
        if (!signal.aborted) {
          setIsError(true)
          handleSearchAPIError(e, FILTER_UPDATE_ERROR_MESSAGE)
        }
      } finally {
        // Only update loading state if this is still the current request
        if (!signal.aborted) {
          setIsLayoutLoading(false)
        }
      }
    },
    [searchQuery],
  )

  const { current: debouncedFetchProductsOnFilterUpdate } = useRef(debounce(fetchProductsOnFilterUpdate, DEBOUNCE_TIME))

  const updateQuery = useCallback(
    ({ filter }: UpdateQueryParams) => {
      const updatedSelectedFilters = getUpdatedSelectedFilters({ filter, selectedFilters })

      if (SelectedFilter.checkEquality(updatedSelectedFilters, selectedFilters)) return
      setWatchedQueryStates(
        {
          filter: SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters),
          q: searchQuery,
        },
        null,
      )

      debouncedFetchProductsOnFilterUpdate({ updatedSelectedFilters })
    },
    [searchQuery, selectedFilters, setWatchedQueryStates, debouncedFetchProductsOnFilterUpdate],
  )

  // Fetch initial search results with facet list
  useEffect(() => {
    async function fetchInitialResults() {
      try {
        setIsLayoutLoading(true)
        const apiFilters = getSearchFilters(selectedFilters)
        const requestParams = {
          query: searchQuery,
          lbUserId,
          limit: LIMIT,
        }

        let response: AxiosResponse<LayoutResponse>

        if (apiFilters.length > 0) {
          // Make parallel calls when filters are applied. When filters are passed to algolia, it doesnt return complete filter list.
          const [layoutResponseWithoutFilters, layoutResponseWithFilters] = await Promise.all([
            fetchSearchLayout(requestParams),
            fetchSearchLayout({
              ...requestParams,
              filter: apiFilters,
            }),
          ])
          const filtersData = convertFacetsToInitialFilters(
            layoutResponseWithoutFilters.data.widgets[0]?.data?.facetsList,
          )
          setFilters(filtersData)
          response = layoutResponseWithFilters
        } else {
          // Single call when no filters are applied
          response = await fetchSearchLayout(requestParams)
          const filtersData = convertFacetsToInitialFilters(response.data.widgets[0]?.data?.facetsList)
          setFilters(filtersData)
        }

        currentPageRef.current = 1
        widgetInfoRef.current = response.data.widgets[0]?.widgetInfo ?? {}
        totalPagesRef.current = response.data.widgets[0]?.data?.paginationMetadata?.totalPages ?? 0
        totalProductsRef.current = response.data.widgets[0]?.data?.paginationMetadata?.totalCount ?? 0
        setProductCards(transformProductsToProductCards(response.data.widgets[0]?.data?.products ?? []))

        // Note: Event is trigerred only when user updates the search query from searchBox.
        if (shouldTriggerSearchCompleteEvent) {
          const eventData = getSearchCompletedEventData({
            data: response.data.widgets[0]?.data?.products ?? [],
            searchQuery: searchQuery.trim(),
            numberOfSearchResults: response.data.widgets[0]?.data?.paginationMetadata?.totalCount ?? 0,
          })
          trackEvent(TELEMETRY_EVENT_NAMES.SEARCH_COMPLETED, eventData)
          siteStateManager.setShouldTriggerSearchCompleteEvent(false)
        }
        siteStateManager.setSearchAlgoliaQueryId({
          searchQuery: searchQuery,
          searchAlgoliaQueryId: response.data.widgets[0]?.data?.searchEventMetadata?.queryId ?? '',
        })
      } catch (e) {
        setIsError(true)
        handleSearchAPIError(e, INITIAL_SEARCH_ERROR_MESSAGE)
      }
      setIsLayoutLoading(false)
    }

    if (searchQuery) {
      trackEvent(TELEMETRY_EVENT_NAMES.SEARCH, {
        searchQuery: searchQuery.trim(),
      })
    }
    void fetchInitialResults()
  }, [searchQuery])

  const intersectionOffset = isBrowser() ? window.innerHeight / 4 : 200
  const [intersectRef, isIntersecting] = useIntersect(null, `${intersectionOffset}px`, 0, false, !hasMorePageToLoad)

  const onLoadMore = useCallback(
    async ({ appliedFilters = selectedFilters }: { appliedFilters?: SelectedFilter }) => {
      if (isMoreProductsLoading || isLayoutLoading) return

      setIsMoreProductsLoading(true)
      let response: AxiosResponse<WidgetFetchDataResponse> | undefined
      try {
        const apiFilters = getSearchFilters(appliedFilters)

        response = await fetchSearchWidgetsData({
          query: searchQuery,
          offset: currentPageRef.current * LIMIT,
          limit: LIMIT,
          lbUserId,
          pageType: PAGE_TYPES.SEARCH,
          widgetInfo: widgetInfoRef.current,
          filter: apiFilters,
        })

        currentPageRef.current++
        const moreProducts = response.data.widgets.data?.products ?? []
        setProductCards(prev => [...prev, ...transformProductsToProductCards(moreProducts)])
        siteStateManager.setSearchAlgoliaQueryId({
          searchQuery: searchQuery,
          searchAlgoliaQueryId: response.data.widgets.data?.searchEventMetadata?.queryId ?? '',
        })
      } catch (e) {
        handleSearchAPIError(e, LOAD_MORE_ERROR_MESSAGE)
      }
      setIsMoreProductsLoading(false)
    },
    [selectedFilters, isMoreProductsLoading, isLayoutLoading, searchQuery],
  )

  useEffect(() => {
    if (isIntersecting && !isMoreProductsLoading && !isLayoutLoading && hasMorePageToLoad) {
      void onLoadMore({ appliedFilters: selectedFilters })
    }
  }, [isIntersecting])

  return {
    productCards,
    isLayoutLoading,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts: totalProductsRef.current,
    filters,
    selectedFilters,
    updateQuery,
  }
}
